// 가게 계산 — 작은 가게 하나의 숫자(평수·테이블·주문·원가·시급…)를 주고, 쪼개서 어림하게 한다.
// '서울 치킨집 수' 같은 페르미 추정은 정답을 확인할 데이터가 없어서 채점이 안 된다 → 계산에 필요한 숫자는 문제에 다 주고,
// 무엇을 어떤 순서로 곱하고 나눌지(문제 쪼개기)와 빠른 어림을 연습한다. 돈은 ±5% 안이면 정답, 사람·기계 수는 정확히(올림·내림 판단까지).
// 레벨 5부터는 계산에 필요 없는 숫자가 섞인다 — 필요한 숫자만 골라내는 것도 실력.
import { int, makeRng, pick, shuffle, type Rng } from './rng';
import type { NumberQuestion } from './NumberDrill';

export interface BizQuestion extends NumberQuestion {
  key: string;
  shop: string;
  lines: string[];
  ask: string;
  unit: string;
  steps: string[];
}

type Made = { shop: string; lines: string[]; ask: string; answer: number; unit: string; tol?: number; tolAbs?: number; signed?: boolean; steps: string[] } | null;
interface BizType { key: string; level: number; usesArea?: boolean; make: (r: Rng) => Made }

export const BIZ_COUNT = 5;
/** 2026년 최저 시급 */
export const MIN_WAGE = 10320;
const W = MIN_WAGE;

const n = (x: number) => x.toLocaleString('ko-KR', { maximumFractionDigits: 2 });
const won = (x: number) => `${n(x)}원`;
const man = (x: number) => `${n(x)}만원`;
const r1 = (x: number) => Math.round(x * 10) / 10;
/** 나눗셈 결과를 올림할 때의 풀이 한 줄 */
const up = (x: number, k: number, unit: string) => (Number.isInteger(x) ? `${k}${unit}` : `${r1(x)} → ${k}${unit} (쪼갤 수 없으니 올림)`);
const signed = (x: number) => `${x > 0 ? '+' : x < 0 ? '−' : ''}${n(Math.abs(x))}`;

export const BIZ_TYPES: BizType[] = [
  { key: 'margin1', level: 1, make: (r) => {
    const P = pick(r, [17000, 18000, 19000, 20000, 21000, 22000]), c = int(r, 11, 16) * 500, pk = int(r, 5, 10) * 100;
    const ans = P - c - pk;
    return { shop: '치킨집', lines: [`후라이드 1마리 ${won(P)}`, `재료비(닭·기름·파우더) ${won(c)}`, `포장 용기·소스 ${won(pk)}`],
      ask: '1마리 팔면 얼마 남을까? (월세·인건비는 빼고)', answer: ans, unit: '원', steps: [`${n(P)} − ${n(c)} − ${n(pk)} = ${n(ans)}원`] };
  } },
  { key: 'daily1', level: 1, make: (r) => {
    const people = int(r, 40, 120), a = int(r, 14, 30) * 500, ans = Math.round((people * a) / 10000);
    return { shop: '분식집', lines: [`하루 손님 ${people}명`, `손님 1명이 평균 ${won(a)}을 쓴다`],
      ask: '하루 매출은 몇 만원?', answer: ans, unit: '만원', tol: 0.05,
      steps: [`${people}명 × ${n(a)}원 = ${n(people * a)}원`, `만원 단위로 ≈ ${n(ans)}만원`] };
  } },
  { key: 'staff1', level: 1, make: (r) => {
    const F = int(r, 12, 18), N = int(r, F + 1, F * 4), k = Math.ceil(N / F);
    return { shop: '치킨집', lines: [`저녁 7~8시에 치킨 주문이 ${N}마리 들어온다`, `튀김 담당 1명이 1시간에 ${F}마리를 튀길 수 있다`, '나도 튀김을 맡는다'],
      ask: '튀김 알바는 최소 몇 명 필요할까?', answer: k - 1, unit: '명',
      steps: [`튀김 인원 = ${N} ÷ ${F} = ${up(N / F, k, '명')}`, `나를 빼면 ${n(k - 1)}명`] };
  } },
  { key: 'monthRev', level: 2, make: (r) => {
    const n1 = int(r, 20, 60), a1 = int(r, 19, 26) * 1000, n2 = int(r, 5, 25), a2 = int(r, 6, 10) * 5000, d = int(r, 25, 30);
    const day = n1 * a1 + n2 * a2, ans = Math.round((day * d) / 10000);
    return { shop: '치킨집', lines: [`하루 배달 ${n1}건, 1건 평균 ${won(a1)}`, `하루 홀 손님 ${n2}팀, 1팀 평균 ${won(a2)}`, `한 달 ${d}일 영업`],
      ask: '한 달 매출은 몇 만원?', answer: ans, unit: '만원', tol: 0.05,
      steps: [`배달 = ${n1} × ${n(a1)} = ${n(n1 * a1)}원`, `홀 = ${n2} × ${n(a2)} = ${n(n2 * a2)}원`, `하루 ${n(day)}원 × ${d}일 = ${n(day * d)}원 ≈ ${n(ans)}만원`] };
  } },
  { key: 'labor1', level: 2, make: (r) => {
    const h = int(r, 4, 8), k = int(r, 3, 6), hours = h * k * 4, total = hours * W, ans = Math.round(total / 10000);
    return { shop: '카페', lines: [`시급 ${won(W)} (2026년 최저 시급)`, `알바가 하루 ${h}시간, 주 ${k}일 일한다`, '한 달은 4주로 치고, 주휴수당은 이번엔 뺀다'],
      ask: '알바 1명의 한 달 인건비는 몇 만원?', answer: ans, unit: '만원', tol: 0.05,
      steps: [`주 ${h * k}시간 × 4주 = ${hours}시간`, `${hours}시간 × ${n(W)}원 = ${n(total)}원 ≈ ${n(ans)}만원`] };
  } },
  { key: 'staffHall', level: 2, make: (r) => {
    const S = int(r, 5, 8), T = int(r, S + 1, S * 4), ans = Math.ceil(T / S);
    return { shop: '고깃집', lines: [`홀 테이블 ${T}개, 저녁엔 다 찬다`, `서빙 1명이 테이블 ${S}개까지 맡을 수 있다`, '나는 주방만 본다'],
      ask: '홀 서빙 알바는 최소 몇 명?', answer: ans, unit: '명', steps: [`${T} ÷ ${S} = ${up(T / S, ans, '명')}`] };
  } },
  { key: 'staffPeak', level: 3, make: (r) => {
    const T = int(r, 3, 10) * 2, c = pick(r, [1, 1.5, 2]), D = int(r, 5, 25), F = int(r, 12, 18), S = int(r, 5, 8);
    const chick = T * c + D, k = Math.ceil(chick / F), h = Math.ceil(T / S), ans = k + h - 1;
    return { shop: '치킨집', lines: [
      `홀 테이블 ${T}개, 피크 1시간엔 다 찬다`, `테이블마다 평균 ${c}마리 주문`, `같은 시간 배달 주문 ${D}마리 (배달은 대행업체가 한다)`,
      `튀김 1명이 1시간에 ${F}마리`, `서빙 1명이 테이블 ${S}개 담당`, '나는 튀김을 맡는다',
    ], ask: '피크 1시간을 버티려면 알바가 최소 몇 명?', answer: ans, unit: '명',
    steps: [`치킨 = ${T} × ${c} + ${D} = ${n(chick)}마리`, `튀김 = ${n(chick)} ÷ ${F} = ${up(chick / F, k, '명')}`, `서빙 = ${T} ÷ ${S} = ${up(T / S, h, '명')}`, `${k} + ${h} − 나 1명 = ${n(ans)}명`] };
  } },
  { key: 'breakeven1', level: 3, make: (r) => {
    const rent = int(r, 3, 10) * 50, labor = int(r, 4, 16) * 50, etc = int(r, 5, 20) * 10, v = pick(r, [55, 58, 60, 62, 65, 68, 70]);
    const K = rent + labor + etc, ans = Math.round((K * 100) / (100 - v));
    return { shop: pick(r, ['치킨집', '카페', '분식집']), lines: [`월세 ${man(rent)}`, `인건비 ${man(labor)}`, `전기·가스·기타 ${man(etc)}`, `재료비·배달 수수료 = 매출의 ${v}%`],
      ask: '한 달 매출이 최소 얼마여야 적자가 아닐까? (손익분기 매출, 만원)', answer: ans, unit: '만원', tol: 0.05,
      steps: [`고정비 = ${rent} + ${labor} + ${etc} = ${n(K)}만원`, `매출 100원마다 ${100 - v}원이 남는다 (100 − ${v})`, `${n(K)} ÷ ${(100 - v) / 100} ≈ ${n(ans)}만원`] };
  } },
  { key: 'cafeCost', level: 3, make: (r) => {
    const c = int(r, 18, 36) * 1000, g = int(r, 17, 21), cup = int(r, 10, 20) * 10;
    const bean = (c * g) / 1000, ans = Math.round(bean + cup);
    return { shop: '카페', lines: [`원두 1kg ${won(c)}`, `아메리카노 1잔에 원두 ${g}g`, `컵·뚜껑·홀더 ${won(cup)}`],
      ask: '아메리카노 1잔 원가(원두 + 컵)는 몇 원?', answer: ans, unit: '원', tol: 0.05,
      steps: [`원두 1g = ${n(c)} ÷ 1,000 = ${n(c / 1000)}원`, `원두 ${g}g = ${n(bean)}원`, `${n(bean)} + 컵 ${n(cup)} ≈ ${n(ans)}원`] };
  } },
  { key: 'breakevenDay', level: 4, make: (r) => {
    const K = int(r, 10, 30) * 50, a = int(r, 15, 28) * 1000, b = Math.round((a * pick(r, [0.45, 0.5, 0.55, 0.6, 0.65])) / 100) * 100, d = int(r, 25, 30);
    const m = a - b, month = (K * 10000) / m, day = month / d, ans = Math.ceil(day);
    return { shop: '치킨집', lines: [`한 달 고정비(월세·인건비 등) ${man(K)}`, `주문 1건 평균 ${won(a)}`, `주문 1건의 재료비·수수료 ${won(b)}`, `한 달 ${d}일 영업`],
      ask: '하루에 최소 몇 건 팔아야 본전일까?', answer: ans, unit: '건', tolAbs: 1,
      steps: [`1건 남는 돈 = ${n(a)} − ${n(b)} = ${n(m)}원`, `한 달 = ${n(K * 10000)} ÷ ${n(m)} ≈ ${n(Math.round(month))}건`, `하루 = ${n(Math.round(month))} ÷ ${d} = ${up(day, ans, '건')}`] };
  } },
  { key: 'payback', level: 4, make: (r) => {
    const I = int(r, 6, 24) * 500, R = int(r, 15, 50) * 100, v = pick(r, [55, 60, 65, 70]), K = int(r, 8, 25) * 50;
    const keep = (R * (100 - v)) / 100, P = keep - K;
    if (P < 150) return null;
    const months = I / P;
    if (months < 6 || months > 60) return null;
    const ans = Math.ceil(months);
    return { shop: pick(r, ['카페', '치킨집', '고깃집']), lines: [`인테리어·장비에 ${man(I)} 들었다 (보증금은 돌려받으니 뺀다)`, `한 달 매출 ${man(R)}`, `재료비·수수료 = 매출의 ${v}%`, `월세·인건비 등 고정비 ${man(K)}`],
      ask: '투자금을 다 되찾는 데 몇 개월 걸릴까?', answer: ans, unit: '개월', tolAbs: 1,
      steps: [`매출에서 남는 몫 = ${n(R)} × ${(100 - v) / 100} = ${n(keep)}만원`, `한 달 이익 = ${n(keep)} − ${n(K)} = ${n(P)}만원`, `${n(I)} ÷ ${n(P)} = ${up(months, ans, '개월')}`] };
  } },
  { key: 'rentRatio', level: 4, make: (r) => {
    const rent = int(r, 10, 50) * 10, fee = int(r, 2, 8) * 5, p = pick(r, [8, 10, 12, 15]), ans = Math.round(((rent + fee) * 100) / p);
    return { shop: pick(r, ['카페', '분식집', '치킨집']), lines: [`월세 ${man(rent)}, 관리비 ${man(fee)}`, `월세·관리비 합이 매출의 ${p}%를 넘지 않게 하고 싶다`],
      ask: '그러려면 한 달 매출이 최소 몇 만원?', answer: ans, unit: '만원', tol: 0.03,
      steps: [`월세 + 관리비 = ${n(rent + fee)}만원`, `매출 × ${p}% = ${n(rent + fee)}만원`, `매출 = ${n(rent + fee)} ÷ ${p / 100} ≈ ${n(ans)}만원`] };
  } },
  { key: 'discount', level: 5, make: (r) => {
    const P = int(r, 10, 30) * 1000, C = Math.round((P * pick(r, [0.4, 0.45, 0.5, 0.55, 0.6])) / 500) * 500, d = pick(r, [10, 15, 20, 25, 30]);
    const sale = (P * (100 - d)) / 100, m1 = P - C, m2 = sale - C;
    if (m2 < P * 0.08) return null;
    const ans = Math.round((m1 / m2 - 1) * 100);
    return { shop: pick(r, ['카페', '치킨집', '분식집']), lines: [`메뉴 가격 ${won(P)}`, `1개 원가(재료·포장·수수료) ${won(C)}`, `${d}% 할인 행사를 한다`],
      ask: '할인 전과 같은 이익을 내려면 판매량이 몇 % 늘어야 할까?', answer: ans, unit: '%', tol: 0.05, tolAbs: 3,
      steps: [`지금 1개 이익 = ${n(P)} − ${n(C)} = ${n(m1)}원`, `할인가 ${n(sale)}원 → 1개 이익 = ${n(m2)}원`, `${n(m1)} ÷ ${n(m2)} = ${n(Math.round((m1 / m2) * 100) / 100)}배 → ${n(ans)}% 더 팔아야 한다 (할인율보다 훨씬 크다)`] };
  } },
  { key: 'delivery', level: 5, make: (r) => {
    const P = int(r, 18, 26) * 1000, f = pick(r, [6, 8, 10, 12]), dv = pick(r, [2000, 2500, 3000, 3500]), pk = int(r, 3, 8) * 100;
    const fee = (P * f) / 100, ans = fee + dv + pk;
    return { shop: '치킨집', lines: [`치킨 1마리 ${won(P)} (홀·배달 같은 가격)`, `배달앱 수수료 = 주문 금액의 ${f}%`, `배달비 중 가게가 내는 돈 ${won(dv)}`, `배달은 포장 용기 값 ${won(pk)}이 더 든다`, '재료비는 홀·배달 똑같다'],
      ask: '배달로 팔면 홀보다 1마리에 얼마 덜 남을까?', answer: ans, unit: '원', tol: 0.03,
      steps: [`수수료 = ${n(P)} × ${f}% = ${n(fee)}원`, `${n(fee)} + 배달비 ${n(dv)} + 용기 ${n(pk)} = ${n(ans)}원`] };
  } },
  { key: 'labor2', level: 5, make: (r) => {
    const h = int(r, 3, 8), k = int(r, 2, 6), H = h * k;
    if (H > 40) return null;
    const base = H * W, jh = H >= 15 ? (H * W) / 5 : 0, total = (base + jh) * 4, ans = Math.round(total / 10000);
    return { shop: '편의점', lines: [`시급 ${won(W)} (2026년 최저 시급)`, `알바가 하루 ${h}시간, 주 ${k}일 일한다`, '주휴수당: 주 15시간 이상 일하고 개근하면 (주 근로시간 ÷ 40) × 8시간 × 시급', '한 달은 4주로 계산, 개근한다'],
      ask: '이 알바의 한 달 인건비는 몇 만원?', answer: ans, unit: '만원', tol: 0.05,
      steps: H >= 15
        ? [`주 ${H}시간 → 주급 ${n(base)}원`, `주휴수당 = ${H} ÷ 40 × 8 × ${n(W)} = ${n(jh)}원`, `(${n(base)} + ${n(jh)}) × 4주 = ${n(total)}원 ≈ ${n(ans)}만원`]
        : [`주 ${H}시간 → 주급 ${n(base)}원`, '주 15시간 미만이라 주휴수당 없음', `${n(base)} × 4주 = ${n(total)}원 ≈ ${n(ans)}만원`] };
  } },
  { key: 'staffArea', level: 5, usesArea: true, make: (r) => {
    const X = int(r, 20, 45), Y = int(r, 6, 12), Z = pick(r, [2, 2.5, 3]), T = Math.floor((X - Y) / Z);
    if (T < 4) return null;
    const D = int(r, 5, 25), F = int(r, 12, 18), S = int(r, 5, 8), chick = T + D, k = Math.ceil(chick / F), h = Math.ceil(T / S), ans = k + h - 1;
    const tables = Number.isInteger((X - Y) / Z) ? `${T}개` : `${r1((X - Y) / Z)} → ${T}개 (반쪽 테이블은 못 놓으니 내림)`;
    return { shop: '치킨집', lines: [
      `가게 ${X}평 중 주방·카운터가 ${Y}평`, `테이블 하나에 통로 포함 ${Z}평이 든다`, `피크 1시간엔 테이블마다 치킨 1마리, 배달 ${D}마리 (배달은 대행)`,
      `튀김 1명이 1시간에 ${F}마리`, `서빙 1명이 테이블 ${S}개 담당`, '나는 튀김을 맡는다',
    ], ask: '알바는 최소 몇 명?', answer: ans, unit: '명',
    steps: [`테이블 = (${X} − ${Y}) ÷ ${Z} = ${tables}`, `치킨 = ${T} + ${D} = ${chick}마리`, `튀김 = ${chick} ÷ ${F} = ${up(chick / F, k, '명')}`, `서빙 = ${T} ÷ ${S} = ${up(T / S, h, '명')}`, `${k} + ${h} − 나 1명 = ${n(ans)}명`] };
  } },
  { key: 'bottleneck', level: 6, make: (r) => {
    const m = pick(r, [4, 5, 6, 8]), t = pick(r, [10, 12, 15]), per = (m * 60) / t, D = int(r, 30, 120), ans = Math.ceil(D / per);
    if (ans < 2) return null;
    return { shop: '치킨집', lines: [`피크 1시간에 치킨 ${D}마리 주문`, `튀김기 1대에 한 번에 ${m}마리`, `한 번 튀기는 데 ${t}분 (넣고 빼는 시간 포함)`],
      ask: '피크 주문을 다 받으려면 튀김기가 최소 몇 대?', answer: ans, unit: '대',
      steps: [`1대가 1시간에 튀기는 횟수 = 60 ÷ ${t} = ${60 / t}번`, `1대 = ${m} × ${60 / t} = ${per}마리/시간`, `${D} ÷ ${per} = ${up(D / per, ans, '대')}`] };
  } },
  { key: 'inventory', level: 6, make: (r) => {
    const s = int(r, 30, 90), L = int(r, 1, 3), k = int(r, 1, 2), ans = s * (L + k);
    return { shop: '치킨집', lines: [`생닭을 하루 평균 ${s}마리 쓴다`, `주문하면 ${L}일 뒤에 도착한다`, `혹시 몰라 ${k}일치는 늘 남겨 둔다`],
      ask: '재고가 몇 마리 이하로 떨어지면 바로 주문해야 할까?', answer: ans, unit: '마리',
      steps: [`도착할 때까지 쓸 양 = ${s} × ${L} = ${s * L}마리`, `남겨 둘 양 = ${s} × ${k} = ${s * k}마리`, `${s * L} + ${s * k} = ${n(ans)}마리`] };
  } },
  { key: 'menuMix', level: 7, make: (r) => {
    const a = int(r, 2, 6) * 10, b = int(r, 1, Math.max(1, (90 - a) / 10 - 1)) * 10, c = 100 - a - b;
    if (c < 10) return null;
    const [ma, mb, mc] = shuffle(r, [int(r, 10, 14), int(r, 15, 19), int(r, 20, 24)]).map((x) => x * 500);
    const [na, nb, nc] = pick(r, [['후라이드', '양념', '반반'], ['아메리카노', '라떼', '디저트'], ['떡볶이', '김밥', '튀김']]);
    const pa = (ma * a) / 100, pb = (mb * b) / 100, pc = (mc * c) / 100, ans = pa + pb + pc;
    return { shop: na === '후라이드' ? '치킨집' : na === '아메리카노' ? '카페' : '분식집',
      lines: [`${na}: 주문의 ${a}%, 1개 남는 돈 ${won(ma)}`, `${nb}: 주문의 ${b}%, 1개 남는 돈 ${won(mb)}`, `${nc}: 주문의 ${c}%, 1개 남는 돈 ${won(mc)}`],
      ask: '주문 1건에 평균 얼마 남을까? (주문 1건 = 메뉴 1개)', answer: ans, unit: '원', tol: 0.03,
      steps: [`${n(ma)} × ${a / 100} + ${n(mb)} × ${b / 100} + ${n(mc)} × ${c / 100}`, `= ${n(pa)} + ${n(pb)} + ${n(pc)} = ${n(ans)}원`] };
  } },
  { key: 'priceTarget', level: 7, make: (r) => {
    const C = int(r, 12, 24) * 500, f = pick(r, [6, 8, 10]), m = pick(r, [20, 25, 30]), q = 100 - f - m, ans = Math.round((C * 100) / q / 10) * 10;
    return { shop: '치킨집', lines: [`1마리 원가(재료·포장) ${won(C)}`, `배달앱 수수료 = 가격의 ${f}%`, `수수료를 떼고도 가격의 ${m}%는 이익으로 남기고 싶다`],
      ask: '가격을 몇 원으로 정해야 할까?', answer: ans, unit: '원', tol: 0.03,
      steps: [`가격 중 원가로 쓸 수 있는 몫 = 100 − ${f} − ${m} = ${q}%`, `가격 × ${q}% = ${n(C)}원`, `가격 = ${n(C)} ÷ ${q / 100} ≈ ${n(ans)}원`] };
  } },
  { key: 'extraStaff', level: 7, make: (r) => {
    const L = int(r, 15, 25) * 10, g = int(r, 10, 18) * 500, d = int(r, 25, 30), month = (L * 10000) / g, day = month / d, ans = Math.ceil(day);
    return { shop: pick(r, ['치킨집', '고깃집', '카페']), lines: [`알바 1명을 더 쓰면 한 달 인건비 ${man(L)}`, `주문 1건 팔면 ${won(g)} 남는다`, `한 달 ${d}일 영업`],
      ask: '알바 1명 몫을 벌려면 하루 몇 건 더 팔아야 할까?', answer: ans, unit: '건', tolAbs: 1,
      steps: [`한 달 = ${n(L * 10000)} ÷ ${n(g)} ≈ ${n(Math.round(month))}건`, `하루 = ${n(Math.round(month))} ÷ ${d} = ${up(day, ans, '건')}`] };
  } },
  { key: 'seatRevenue', level: 8, make: (r) => {
    const T = int(r, 6, 20), p = pick(r, [2, 4]), o = int(r, 4, 8) * 10, turn = int(r, 2, 5), a = int(r, 8, 20) * 1000, d = int(r, 25, 30);
    const guests = ((T * p * o) / 100) * turn, ans = Math.round((guests * a * d) / 10000);
    return { shop: pick(r, ['고깃집', '카페', '분식집']), lines: [`테이블 ${T}개, 테이블마다 ${p}자리`, `평균적으로 자리의 ${o}%만 찬다`, `하루에 자리가 ${turn}번 바뀐다 (회전)`, `손님 1명이 평균 ${won(a)}`, `한 달 ${d}일 영업`],
      ask: '한 달 홀 매출은 몇 만원?', answer: ans, unit: '만원', tol: 0.05,
      steps: [`자리 = ${T} × ${p} = ${T * p}석`, `하루 손님 = ${T * p} × ${o}% × ${turn}회 = ${n(r1(guests))}명`, `하루 매출 = ${n(r1(guests))} × ${n(a)} = ${n(Math.round(guests * a))}원`, `× ${d}일 ≈ ${n(ans)}만원`] };
  } },
  { key: 'coupon', level: 8, make: (r) => {
    const cv = int(r, 2, 10) * 500, N = int(r, 10, 40) * 10, e = int(r, 3, 8) * 10, g = int(r, 12, 24) * 500;
    const old = (N * e) / 100, fresh = N - old, delta = fresh * (g - cv) - old * cv;
    if (Math.abs(delta) < 100000) return null;
    const ans = Math.round(delta / 10000);
    return { shop: pick(r, ['치킨집', '카페', '분식집']), lines: [`쿠폰 1장에 ${won(cv)} 할인`, `쿠폰을 쓴 주문 ${N}건`, `그중 ${e}%는 쿠폰이 없어도 왔을 단골`, `주문 1건 남는 돈(할인 전) ${won(g)}`],
      ask: '쿠폰 행사로 이익이 몇 만원 변했을까? (늘면 +, 줄면 −)', answer: ans, unit: '만원', tol: 0.05, tolAbs: 1, signed: true,
      steps: [`단골 ${n(old)}건: 어차피 올 손님이라 할인만큼 손해 → −${n(old * cv)}원`, `새 손님 ${n(fresh)}건: (${n(g)} − ${n(cv)}) × ${n(fresh)} = +${n(fresh * (g - cv))}원`, `합계 ${signed(delta)}원 ≈ ${signed(ans)}만원`] };
  } },
  { key: 'ltv', level: 9, make: (r) => {
    const g = int(r, 6, 18) * 500, f = int(r, 1, 4), m = int(r, 3, 24), c = int(r, 1, 6) * 2000, q = pick(r, [20, 25, 50]);
    const life = g * f * m, cost = (c * 100) / q, net = life - cost;
    if (net < 5000) return null;
    return { shop: pick(r, ['카페', '분식집', '고깃집']), lines: [
      `손님이 1번 올 때 남는 돈 ${won(g)}`, `단골은 한 달에 ${f}번 온다`, `평균 ${m}개월 다니다 발길을 끊는다`,
      `광고로 새 손님 1명 데려오는 데 ${won(c)}`, `광고로 온 손님 중 ${q}%만 단골이 된다 (나머지가 한 번 온 건 무시)`,
    ], ask: '단골 1명이 광고비를 빼고 결국 얼마를 남길까?', answer: net, unit: '원', tol: 0.05,
    steps: [`단골 1명이 남기는 돈 = ${n(g)} × ${f}번 × ${m}개월 = ${n(life)}원`, `단골 1명 만드는 광고비 = ${n(c)} ÷ ${q}% = ${n(cost)}원`, `${n(life)} − ${n(cost)} = ${n(net)}원`] };
  } },
  { key: 'hireOrNot', level: 9, make: (r) => {
    const q = int(r, 3, 15), g = int(r, 10, 20) * 500, d = int(r, 25, 30), L = int(r, 10, 20) * 10;
    const gain = (q * g * d) / 10000, delta = gain - L;
    if (Math.abs(delta) < 10) return null;
    const ans = Math.round(delta);
    return { shop: pick(r, ['치킨집', '고깃집']), lines: [`일손이 모자라 피크에 하루 ${q}건씩 주문을 놓친다`, '알바 1명을 쓰면 놓치는 주문을 다 받을 수 있다', `주문 1건 남는 돈 ${won(g)}`, `한 달 ${d}일 영업`, `알바 1명 한 달 인건비 ${man(L)}`],
      ask: '알바를 쓰면 한 달 이익이 몇 만원 변할까? (늘면 +, 줄면 −)', answer: ans, unit: '만원', tol: 0.05, tolAbs: 1, signed: true,
      steps: [`더 받는 주문 = ${q} × ${d}일 = ${q * d}건`, `더 남는 돈 = ${q * d} × ${n(g)} = ${n(q * g * d)}원 ≈ ${n(r1(gain))}만원`, `${n(r1(gain))} − 인건비 ${n(L)} ≈ ${signed(ans)}만원 → ${ans > 0 ? '쓰는 게 이득' : '안 쓰는 게 낫다'}`] };
  } },
  { key: 'fullPL', level: 10, make: (r) => {
    const cnt = int(r, 40, 120), a = int(r, 18, 25) * 1000, d = int(r, 26, 30), mat = int(r, 30, 40), y = int(r, 4, 8) * 10, f = pick(r, [8, 10, 12]);
    const rent = int(r, 15, 40) * 10, k = int(r, 1, 3), hrs = int(r, 4, 8), etc = int(r, 5, 15) * 10;
    const R = (cnt * a * d) / 10000, material = (R * mat) / 100, fee = (((R * y) / 100) * f) / 100, labor = (k * hrs * d * W) / 10000;
    const profit = R - material - fee - labor - rent - etc;
    if (profit < 50) return null;
    const ans = Math.round(profit);
    return { shop: '치킨집', lines: [
      `하루 주문 ${cnt}건, 1건 평균 ${won(a)}, 한 달 ${d}일 영업`, `재료비 = 매출의 ${mat}%`, `매출의 ${y}%가 배달이고, 배달 매출의 ${f}%가 앱 수수료`,
      `알바 ${k}명이 영업일마다 ${hrs}시간씩 (시급 ${won(W)}, 주휴수당은 빼고)`, `월세 ${man(rent)}, 전기·가스·기타 ${man(etc)}`,
    ], ask: '한 달에 사장 손에 남는 돈은 몇 만원? (세금 내기 전)', answer: ans, unit: '만원', tol: 0.05,
    steps: [
      `매출 = ${cnt} × ${n(a)} × ${d} ≈ ${n(Math.round(R))}만원`, `재료비 = 매출 × ${mat}% ≈ ${n(Math.round(material))}만원`,
      `앱 수수료 = 매출 × ${y}% × ${f}% ≈ ${n(Math.round(fee))}만원`, `인건비 = ${k}명 × ${hrs}시간 × ${d}일 × ${n(W)} ≈ ${n(Math.round(labor))}만원`,
      `남는 돈 = 매출 − 재료비 − 수수료 − 인건비 − 월세 ${rent} − 기타 ${etc} ≈ ${n(ans)}만원`,
    ] };
  } },
];

/** 계산에 필요 없는 숫자 — 레벨 5부터 섞어서 필요한 숫자를 골라내게 한다 */
const NOISE: { area?: boolean; make: (r: Rng) => string }[] = [
  { area: true, make: (r) => `가게는 ${int(r, 12, 40)}평이다` },
  { make: (r) => `사장 경력 ${int(r, 2, 15)}년` },
  { make: (r) => `주차 공간 ${int(r, 1, 6)}대` },
  { make: (r) => `메뉴는 ${int(r, 8, 30)}가지` },
  { make: (r) => `리뷰 별점 4.${int(r, 1, 9)}` },
  { make: (r) => `개업한 지 ${int(r, 3, 36)}개월` },
];

export function makeBizRound(level: number, seed: number): BizQuestion[] {
  const rng = makeRng(seed);
  const lv = Math.min(10, Math.max(1, level));
  const pool = BIZ_TYPES.filter((t) => t.level <= Math.max(lv, 2) && t.level >= lv - 2);
  const exact = pool.filter((t) => t.level === lv);
  const newest = exact.length ? exact : pool;
  const limitSec = lv <= 2 ? 60 : lv <= 4 ? 90 : lv <= 6 ? 120 : lv <= 8 ? 150 : 180;
  const noiseCount = lv >= 8 ? 2 : lv >= 5 ? 1 : 0;
  const out: BizQuestion[] = [];
  for (let guard = 0; out.length < BIZ_COUNT && guard < 500; guard++) {
    const t = rng() < 0.5 ? pick(rng, newest) : pick(rng, pool);
    const fresh = pool.some((p) => !out.some((q) => q.key === p.key));
    if (fresh && out.some((q) => q.key === t.key)) continue; // 유형이 남아 있으면 같은 유형은 한 판에 한 번
    const made = t.make(rng);
    if (!made) continue;
    const lines = [...made.lines];
    for (const noise of shuffle(rng, NOISE.filter((x) => !(x.area && t.usesArea))).slice(0, noiseCount)) {
      lines.splice(int(rng, 0, lines.length), 0, noise.make(rng));
    }
    out.push({ ...made, key: t.key, lines, limitSec, explain: made.steps.join(' → ') });
  }
  return out;
}

/** 허용 오차 안내 — 문제 아래에 보여 준다 */
export function toleranceText(q: Pick<NumberQuestion, 'tol' | 'tolAbs'>, unit: string): string {
  if (q.tol) return `어림해도 됩니다 — ±${Math.round(q.tol * 100)}% 안이면 정답${q.tolAbs ? ` (작은 수는 ±${q.tolAbs}${unit})` : ''}`;
  if (q.tolAbs) return `±${q.tolAbs}${unit} 안이면 정답`;
  return '정확히 맞혀야 합니다 (올림·내림 주의)';
}
